import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';

/** Режим — что выбрал человек; тема — что в итоге на экране. */
export type ThemeMode = 'system' | Theme;

const STORAGE_KEY = 'flowdo.theme';

// Событие для остальных экземпляров хука на странице.
const THEME_EVENT = 'flowdo:theme';

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);

    return value === 'light' || value === 'dark' ? value : null;
  } catch {
    // Приватный режим или запрет на хранилище — не повод падать.
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Текущая тема интерфейса.
 *
 * Состояний три. Пока пользователь не выбрал тему сам, `data-theme` на корне
 * не ставится вовсе: тогда работает `prefers-color-scheme`, и смена
 * системной темы видна сразу. Как только выбор сделан — он записывается на
 * корень и в хранилище, и системная тема его больше не перебивает. Режим
 * «как в системе» возвращает всё назад.
 */
export function useTheme() {
  const [stored, setStored] = useState<Theme | null>(readStored);
  const [system, setSystem] = useState<Theme>(systemTheme);

  const mode: ThemeMode = stored ?? 'system';
  const theme: Theme = stored ?? system;

  useEffect(() => {
    const root = document.documentElement;

    if (stored) {
      root.dataset['theme'] = stored;
    } else {
      delete root.dataset['theme'];
    }
  }, [stored]);

  // За системной темой следим всегда: режим «как в системе» может быть
  // выбран позже, и к тому моменту значение должно быть актуальным.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      setSystem(media.matches ? 'dark' : 'light');
    };

    media.addEventListener('change', onChange);

    return () => {
      media.removeEventListener('change', onChange);
    };
  }, []);

  // Хук живёт в нескольких местах (меню пользователя, палитра ⌘K): смена
  // в одном должна отразиться в остальных, иначе галочка в меню отстаёт.
  useEffect(() => {
    const sync = () => {
      setStored(readStored());
    };

    window.addEventListener(THEME_EVENT, sync);

    return () => {
      window.removeEventListener(THEME_EVENT, sync);
    };
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    const value = next === 'system' ? null : next;

    setStored(value);

    try {
      if (value) {
        localStorage.setItem(STORAGE_KEY, value);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Тема останется выбранной на время сессии — это лучше, чем ошибка.
    }

    window.dispatchEvent(new Event(THEME_EVENT));
  }, []);

  const toggle = useCallback(() => {
    setMode(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setMode]);

  return { theme, mode, setMode, toggle };
}
